
import simplify from 'simplify-js';
import * as d3 from 'd3';

/**^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
 * Finds the maximum area rectangle that fits inside a polygon
 * Modified from https://d3plus.org/blog/behind-the-scenes/2014/07/08/largest-rect/
 *------------------------------------------------------------------------------
 *
 * @param {Array} poly - is an array of two-dimensional arrays corresponding to 
 *  the points of the polygon.
 *
 * @param {Object} options - is a dictionary of options with attributes
 * @param {(number|string|Array)} [options.angle] - specifies the rotation of the polygon. 
 *  An angle of zero means that the longer side of the polygon (the width) will 
 *  be aligned with the x axis. An angle of +90 and/or -90 means that the longer 
 *  side of the polygon (the width) will be aligned with the y axis. The parameter 
 *  angle can be
 *     - a number between -90 and +90 specifying the angle of rotation of the polygon.  
 *     - a string which is parsed to a number
 *     - an array of numbers, specifying the possible rotations of the polygon
 *     - unspecified, which means the polygon can have any possible angle
 *
 * @param {(number|string|Array)} options.aspectRatio - the ratio between the 
 *  width and the height of the rectangle, i.e. width/height. The parameter 
 *  aspectRatio can be
 *     - a number
 *     - a string which is parsed to a number
 *     - an array of numbers, specifying the possible aspectRatios of the polygon
 *
 * @param {number} options.maxAspectRatio - maximum aspect ratio (width/height). 
 *  Default is 15. This should be used if the aspectRatio is not provided.
 *
 * @param {number} options.nTries - the number of randomly drawn points inside the 
 *  polygon which the algorithm explores as possible center points of the maximal 
 *  rectangle. Default value is 20.
 *
 * @param {number} options.minWidth - the minimum width of the rectangle. 
 *  Default is 0.
 *
 * @param {number} options.minHeight - the minimum height of the rectangle. 
 *  Default is 0.
 *
 * @param {number} options.tolerance - The simplification tolerance factor. 
 *  Should be between 0 and 1. Default is 0.02. Larger tolerance corresponds to 
 *  more extensive simplification.
 *
 * @param {Array} options.origin - the center point of the rectangle. If 
 *  specified, the rectangle is fixed at that point, otherwise the algorithm 
 *  optimizes across all possible points. The parameter origin can be
 *     - a two dimensional array specifying the x and y coordinate of the origin
 *     - an array of two dimensional arrays specifying the the possible center points
 *     of the maximal rectangle.
 *
 * @return Returns the largest found rectangle as an object with the following attributes
 *  width - the width of the rectangle
 *  height - the height of the rectangle
 *  cx - the x coordinate of the rectangle's center
 *  cy - the y coordinate of the rectangle's center
 *  angle - rotation angle in degrees. The anchor of rotation is the center point
 */
export const largestRect = (poly, options) => {
  /* For visualization debugging purposes */
  let events = [];
  const debug = event => { if (options.vdebug) events.push(event); };
  
  if (!poly || poly.length < 4) {
    throw new Error('Not a polygon');
  }

  /********* Algorithm constants **********/
  // step size for the aspect ratio
  const aspectRatioStep = 0.5;
  // step size for angles (in degrees); has linear impact on running time
  const angleStep = 5;
  /****************************************/

  /**** User's input normalization ****/
  // maximum allowed aspect ratio for the rectangle solution
  options = Object.assign({}, {
    maxAspectRatio: 15,
    minWidth: 0,
    minHeight: 0,
    tolerance: 0.02,
    nTries: 20 // Default value for the number of possible center points of the maximal rectangle
  }, options);

  const getArrayOption = (option, defaultValue) => {
    if (exists(option)) {
      if (option instanceof Array) return option;
      else if (typeof option == 'number') return [option];
      else if (typeof option == 'string' && !isNaN(option)) return [Number(option)];
    }
    return defaultValue;
  };
  
  const angles = getArrayOption(options.angle, d3.range(-90, 90+angleStep, angleStep));
  const aspectRatios = getArrayOption(options.aspectRatio);
  let origins = (origin => {
    if (exists(origin)) {
      if (origin instanceof Array) {
        if (origin[0] instanceof Array) return origin;
        else return [origin];
      }
    }
    return null;
  })(options.origin);


  /**************************************/
  const area = d3.polygonArea(poly);

  if (area === 0) {
    console.warn ('Polygon has zero area');
      
    return {};
  }

  // get the width of the bounding box of the original polygon to determine tolerance
  let [minx, maxx] = d3.extent( poly, d => d[0] );
  let [miny, maxy] = d3.extent( poly, d => d[1] );

  // simplify polygon
  const tolerance = Math.min(maxx - minx, maxy - miny) * options.tolerance;
  if (tolerance > 0) {
    poly = simplify( poly, tolerance );
    debug({ type: 'simplify', poly: poly });
    
    // get the width of the bounding box of the simplified polygon
    [minx, maxx] = d3.extent( poly, d => d[0] );
    [miny, maxy] = d3.extent( poly, d => d[1] );
  }
  
  //const bBox = [[minx, miny], [maxx, miny], [maxx, maxy] ,[minx, maxy]]; // IS THIS USED
  const [boxWidth, boxHeight] = [maxx - minx, maxy - miny];
  
  // discretize the binary search for optimal width to a resolution of this times the polygon width
  const widthStep = Math.min(boxWidth, boxHeight)/50;
  
  // populate possible center points with random points inside the polygon
  if (!exists(origins)) {
    origins = [];
    // get the centroid of the polygon
    const centroid = d3.polygonCentroid(poly);
    if (pointInPoly(centroid, poly)) origins.push(centroid);
    // get few more points inside the polygon
    while (origins.length < options.nTries) {
      const rndX = Math.random() * boxWidth + minx;
      const rndY = Math.random() * boxHeight + miny;
      const rndPoint = [rndX, rndY];
      if (pointInPoly(rndPoint, poly)) origins.push(rndPoint);
    }
  }
  debug({ type: 'origins', points: origins });
  let maxArea = 0;
  let maxRect = null;
  
  angles.forEach(angle => {
    const angleRad = -angle * Math.PI / 180;
    debug({ type: 'angle', angle });
    
    origins.forEach((origOrigin, origIndex) => {
      // generate improved origins
      let [p1W, p2W] = intersectPoints( poly, origOrigin, angleRad );
      let [p1H, p2H] = intersectPoints( poly, origOrigin, angleRad + Math.PI/2 );
      const modifOrigins = [
        [(p1W[0] + p2W[0])/2, (p1W[1] + p2W[1])/2], // average along with width axis
        [(p1H[0] + p2H[0])/2, (p1H[1] + p2H[1])/2] // average along with height axis
      ];
      debug({ type: 'modifOrigin', idx: origIndex, p1W, p2W, p1H, p2H, modifOrigins });
      
      modifOrigins.forEach(origin => {
        debug({ type: 'origin', cx: origin[0], cy: origin[1] });
        [p1W, p2W] = intersectPoints( poly, origin, angleRad );
        const minSqDistW = Math.min( squaredDist(origin, p1W), squaredDist(origin, p2W) );
        const maxWidth = 2*Math.sqrt(minSqDistW);
        
        [p1H, p2H] = intersectPoints( poly, origin, angleRad + Math.PI/2 );
        const minSqDistH = Math.min( squaredDist(origin, p1H), squaredDist(origin, p2H) );
        const maxHeight = 2*Math.sqrt(minSqDistH);
        
        let isBiggerThanCurrentMaxArea = maxWidth * maxHeight >= maxArea;
        
        if (isBiggerThanCurrentMaxArea) {
          
          let aRatios;
            
          if (exists(aspectRatios)) aRatios = aspectRatios;
          else {
            const {maxAspectRatio, minWidth, minHeight} = options;
            const _minAspectRatio = Math.max( 1, minWidth / maxHeight, maxArea/(maxHeight*maxHeight) );
            const _maxAspectRatio = Math.min( maxAspectRatio, maxWidth/minHeight, (maxWidth*maxWidth)/maxArea );
            aRatios = d3.range(_minAspectRatio, _maxAspectRatio + aspectRatioStep, aspectRatioStep);
          }
          
          aRatios.forEach(aRatio => {
            // do a binary search to find the max width that works
            let left = Math.max( options.minWidth, Math.sqrt(maxArea*aRatio) );
            let right = Math.min( maxWidth, maxHeight*aRatio );
            
            isBiggerThanCurrentMaxArea = right * maxHeight >= maxArea;
            
            if (isBiggerThanCurrentMaxArea) {
            
              if ((right - left) >= widthStep) debug({ type: 'aRatio', aRatio });
              
              while ((right - left) >= widthStep) {
                const width = (left + right) / 2;
                const height = width / aRatio;
                const [x0, y0] = origin;
                const halfWidth = width/2;
                const halfHeight = height/2;
                let insidePoly;
                let rectPoly = [
                  [x0 - halfWidth, y0 - halfHeight],
                  [x0 + halfWidth, y0 - halfHeight],
                  [x0 + halfWidth, y0 + halfHeight],
                  [x0 - halfWidth, y0 + halfHeight]
                ];
                rectPoly = rotatePoly( rectPoly, angleRad, origin );
                if (polyInsidePoly(rectPoly, poly)) {
                  insidePoly = true;
                  // we know that the area is already greater than the maxArea found so far
                  maxArea = width * height;
                  maxRect = {
                    tx: -halfWidth,
                    ty: -halfHeight,
                    cx: x0,
                    cy: y0,
                    width,
                    height,
                    angle
                  };
                  left = width; // increase the width in the binary search
                }
                else {
                  insidePoly = false;
                  right = width; // decrease the width in the binary search
                }
                debug({
                  type: 'rectangle',
                  cx: x0,
                  cy: y0,
                  width,
                  height,
                  areaFraction: (width*height)/area,
                  angle,
                  insidePoly 
                });
              }
            }
          });
        }
      });
    });
  });
  return {maxRect, maxArea, events};
};


//^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^^
// Helper functions
//------------------------------------------------------------------------------

// Return true if argument is not undefined nor null
export const exists = obj => typeof obj !== 'undefined' && obj !== null;

// Returns the squared euclidean distance between points a and b
export const squaredDist = (a, b) => {
  const deltax = b[0] - a[0];
  const deltay = b[1] - a[1];
  return deltax*deltax + deltay*deltay;
};


// Checks whether the horizontal ray going through point p intersects the segment p1p2
// Implementation from: http://rosettacode.org/wiki/Ray-casting_algorithm#CoffeeScript
const rayIntersectsSegment = (p, p1, p2) => {
  const [a, b] = p1[1] < p2[1] ? [p1, p2] : [p2, p1];
  if (p[1] == b[1] || p[1] == a[1])
    p[1] += Number.MIN_VALUE;
  if (p[1] > b[1] || p[1] < a[1]) return false;
  else if (p[0] > a[0] && p[0] > b[0]) return false;
  else if (p[0] < a[0] && p[0] < b[0]) return true;
  else {
    const mAB = (b[1] - a[1]) / (b[0] - a[0]);
    const mAP = (p[1] - a[1]) / (p[0] - a[0]);
    return mAP > mAB;
  }
};

// Checks whether the point p is inside a polygon using the Ray-Casting algorithm
// Implementation from: http://rosettacode.org/wiki/Ray-casting_algorithm#CoffeeScript
export const pointInPoly = (p, poly) => {
  let i = -1;
  const n = poly.length;
  let a, 
      b = poly[n-1], 
      c = 0;
  
  while (++i < n) {
    a = b;
    b = poly[i];
    rayIntersectsSegment(p, a, b) && c++;
  }
  return c % 2 != 0;
};

// Checks whether the point p is inside the bounding box of the line segment p1q1
const pointInSegmentBox = (p, p1, q1) => {
  // allow for some margins due to numerical errors
  const eps = 1e-9;
  const [px, py] = p;
  if (px < Math.min(p1[0], q1[0]) - eps || 
      px > Math.max(p1[0], q1[0]) + eps || 
      py < Math.min(p1[1], q1[1]) - eps || 
      py > Math.max(p1[1], q1[1]) + eps) {
    return false;
  }
  return true;
};

// Finds the intersection point (if there is one) of the lines p1q1 and p2q2
const lineIntersection = (p1, q1, p2, q2) => {
  // allow for some margins due to numerical errors
  const eps = 1e-9;
  // find the intersection point between the two infinite lines
  const dx1 = p1[0] - q1[0];
  const dy1 = p1[1] - q1[1];
  const dx2 = p2[0] - q2[0];
  const dy2 = p2[1] - q2[1];
  const denom = dx1 * dy2 - dy1 * dx2;
  
  if (Math.abs(denom) < eps) return null;
  
  const cross1 = p1[0]*q1[1] - p1[1]*q1[0];
  const cross2 = p2[0]*q2[1] - p2[1]*q2[0];

  const px = (cross1*dx2 - cross2*dx1) / denom;
  const py = (cross1*dy2 - cross2*dy1) / denom;
  return [px, py];
};

// Checks whether the line segments p1q1 and p2q2 intersect
export const segmentsIntersect = (p1, q1, p2, q2) => {
  const p = lineIntersection( p1, q1, p2, q2 );
  if (!exists(p)) return false;
  return pointInSegmentBox(p, p1, q1) && pointInSegmentBox(p, p2, q2);
};

// Check if polygon polyA is inside polygon polyB
const polyInsidePoly = (polyA, polyB) => {
  let iA = -1;
  const nA = polyA.length;
  const nB = polyB.length;
  let aA, bA = polyA[nA-1];
  
  while (++iA < nA) {
    aA = bA;
    bA = polyA[iA];
    
    let iB = -1;
    let aB, bB = polyB[nB-1];
    while (++iB < nB) {
      aB = bB;
      bB = polyB[iB];
      if (segmentsIntersect( aA, bA, aB, bB )) return false;
    }
  }

  return pointInPoly( polyA[0], polyB );
};

// Rotates the point p for alpha radians around the origin 
const rotatePoint = (alpha, origin = [0,0]) => p => {
  const xshifted = p[0] - origin[0];
  const yshifted = p[1] - origin[1];
  const cosAlpha = Math.cos(alpha);
  const sinAlpha = Math.sin(alpha);
  return [ cosAlpha * xshifted - sinAlpha * yshifted + origin[0],
    sinAlpha * xshifted + cosAlpha * yshifted + origin[1]
  ];
};

// Rotates the polygon for alpha radians around the origin  
const rotatePoly = (poly, alpha, origin) => poly.map(rotatePoint(alpha, origin));

// Gives the 2 closest intersection points between a ray with alpha radians
// from the origin and the polygon. The two points should lie on opposite sides of the origin
const intersectPoints = (poly, origin, alpha) => {
  const eps = 1e-9;
  origin = [origin[0] + eps*Math.cos(alpha), origin[1] + eps*Math.sin(alpha)];
  const [x0, y0] = origin;
  const shiftedOrigin = [x0 + Math.cos(alpha), y0 + Math.sin(alpha)];
  
  const idx = (Math.abs(shiftedOrigin[0] - x0) < eps) ? 1 : 0;
  let i = -1;
  const n = poly.length;
  let a, b = poly[n-1];
  let minSqDistLeft = Number.MAX_VALUE;
  let minSqDistRight = Number.MAX_VALUE;
  let closestPointLeft = null;
  let closestPointRight = null;
  while (++i < n) {
    a = b;
    b = poly[i];
    const p = lineIntersection(origin, shiftedOrigin, a, b);
    if (exists(p) && pointInSegmentBox(p, a, b)) {
      const sqDist = squaredDist(origin, p);
      if (p[idx] < origin[idx]) {
        if (sqDist < minSqDistLeft) {
          minSqDistLeft = sqDist;
          closestPointLeft = p;
        }
      }
      else if (p[idx] > origin[idx]) {
        if (sqDist < minSqDistRight) {
          minSqDistRight = sqDist;
          closestPointRight = p;
        }
      }
    }
  }
  return [closestPointLeft, closestPointRight];
};

